import { InspectionSyncResponse } from '../api-contracts';
import { inspectionDB } from '../data/database.structure';
import {
  InspectionModel,
  ObservationModel,
  InspectionCategoryTypeModel,
} from '../data/types';

interface BuildInspectionsOptions {
  payload: InspectionSyncResponse;
  inspectionExternalId?: string;
  preserveExistingObservationsWhenPayloadEmpty?: boolean;
  isFullSync?: boolean;
}

export async function buildInspections({
  payload,
  inspectionExternalId,
  preserveExistingObservationsWhenPayloadEmpty = false,
  isFullSync = false,
}: BuildInspectionsOptions): Promise<InspectionModel[]> {
  if (payload.syncItems && payload.syncItems.inspections) {
    const inspectionCategories =
      await inspectionDB.inspectionCategories.toArray();
    const inspectionTypes = await inspectionDB.inspectionTypes.toArray();
    const observableItems = await inspectionDB.observableItems.toArray();
    const customerMap = new Map(
      (await inspectionDB.customers.toArray()).map((c) => [c.id, c]),
    );
    const mineLocationMap = new Map(
      (await inspectionDB.mineLocations.toArray()).map((ml) => [ml.id, ml]),
    );
    const assetMap = new Map(
      (await inspectionDB.assets.toArray()).map((a) => [a.id, a]),
    );
    const siteMap = new Map(
      (await inspectionDB.sites.toArray()).map((s) => [s.id, s]),
    );
    const payloadContainsObservations =
      (payload.syncItems.observations?.length ?? 0) > 0;

    const inspectionsStructured: InspectionModel[] = [];

    // Build the inspection object
    for (const inspection of payload.syncItems.inspections) {
      if (
        inspectionExternalId &&
        inspection.externalId !== inspectionExternalId
      ) {
        continue;
      }

      // Check Customer/Site/Asset existence
      if (inspection.assetId !== undefined && inspection.assetId > 0) {
        const asset = assetMap.get(inspection.assetId);
        if (!asset) {
          console.warn(
            `Asset with ID ${inspection.assetId} not found for inspection ${inspection.id}`,
          );
          continue;
        }

        if (asset.siteId === undefined) {
          console.warn(`Asset with ID ${asset.id} has no siteId.`);
          continue;
        }

        const site = siteMap.get(asset.siteId);
        if (!site) {
          console.warn(
            `Site with ID ${asset.siteId} not found for asset ${asset.id}`,
          );
          continue;
        }

        const customer = customerMap.get(site.customerId);
        if (!customer) {
          console.warn(
            `Customer with ID ${site.customerId} not found for site ${site.id}`,
          );
          continue;
        }
      } else if (inspection.mineLocationId !== undefined) {
        const mineLocation = mineLocationMap.get(inspection.mineLocationId);
        if (!mineLocation) {
          console.warn(
            `MineLocation with ID ${inspection.mineLocationId} not found for inspection ${inspection.id}`,
          );
          continue;
        }
      } else {
        console.warn(
          `Inspection with ID ${inspection.id} has no assetId or mineLocationId.`,
        );
        continue; // Skip inspection if no asset or mine location is provided
      }

      const inspectionCategoryTypes = (
        payload.syncItems.inspectionCategoryTypes ?? []
      ).filter((category) => category.inspectionId === inspection.id);

      if (inspectionCategoryTypes.length === 0) {
        // No category types in the payload for this inspection — only the top-level
        // properties are available to persist.
        const inspectionDb = await inspectionDB.inspections.get(
          inspection.externalId!,
        );

        const inspectionDate = inspection.inspectionDate
          ? new Date(
              Date.UTC(
                inspection.inspectionDate.getFullYear(),
                inspection.inspectionDate.getMonth(),
                inspection.inspectionDate.getDate(),
                inspection.inspectionDate.getHours(),
                inspection.inspectionDate.getMinutes(),
                inspection.inspectionDate.getSeconds(),
              ),
            )
          : undefined;

        if (inspectionDb) {
          // Delta sync: merge the updated top-level properties into the existing
          // local copy, preserving its category types and observations.
          const inspectionStructured: InspectionModel = {
            ...inspectionDb,
            inspectionDate: inspectionDate,
            equipmentExternalId: inspection.bucketExternalId,
            isSynced: true,
            completed: inspection.completed,
          } as InspectionModel;
          inspectionsStructured.push(inspectionStructured);
        } else {
          // No local copy. On a full sync the table was cleared first, so a miss is
          // expected; on other syncs it is unexpected, so warn. Either way, persist
          // the inspection as a top-level shell instead of dropping it.
          if (!isFullSync) {
            console.warn(
              `Inspection with externalId ${inspection.externalId} not found in database.`,
            );
          }
          const inspectionStructured = {
            ...inspection,
            inspectionDate: inspectionDate,
            equipmentExternalId: inspection.bucketExternalId,
            isSynced: true,
          } as InspectionModel;
          inspectionStructured.inspectionCategoryTypes = [];
          inspectionsStructured.push(inspectionStructured);
        }
        continue;
      }

      // The local copy is only used to retain existing observations when present. On a
      // full sync the inspections table was cleared first, so a miss is expected; on
      // other syncs a miss is unexpected, so warn. The inspection is built from the
      // payload below regardless.
      const inspectionDb = await inspectionDB.inspections.get(
        inspection.externalId!,
      );

      if (!isFullSync && !inspectionDb) {
        console.warn(
          `Inspection with externalId ${inspection.externalId} not found in database.`,
        );
      }

      const categories = inspectionCategories.filter((category) =>
        inspectionCategoryTypes.some(
          (inspectionCategoryType) =>
            inspectionCategoryType.categoryId === category.id,
        ),
      );

      const types = inspectionTypes.filter((type) =>
        inspectionCategoryTypes.some(
          (inspectionCategoryType) => inspectionCategoryType.typeId === type.id,
        ),
      );

      const databaseObs =
        (inspectionDb?.inspectionCategoryTypes ?? []).flatMap(
          (category) => category.observations,
        ) ?? [];

      const payloadObs = (payload.syncItems.observations ?? []).filter(
        (observation) =>
          inspectionCategoryTypes.some(
            (inspectionCategoryType) =>
              inspectionCategoryType.id === observation.categoryTypeId,
          ),
      );

      // Join the obs and observations, if there is duplicates, keep the one in the dababase
      const observations = payloadObs.map((observation) => {
        const existingObservation = databaseObs.find(
          (obs) => obs.externalId === observation.externalId,
        );
        if (existingObservation) {
          return {
            ...existingObservation,
            ...observation,
          } as ObservationModel;
        }
        return observation as ObservationModel;
      });

      const observationItems = observableItems.filter((item) =>
        observations.some(
          (observation) => observation.observableItemId === item.id,
        ),
      );

      const inspectionDate = inspection.inspectionDate
        ? new Date(
            Date.UTC(
              inspection.inspectionDate.getFullYear(),
              inspection.inspectionDate.getMonth(),
              inspection.inspectionDate.getDate(),
              inspection.inspectionDate.getHours(),
              inspection.inspectionDate.getMinutes(),
              inspection.inspectionDate.getSeconds(),
            ),
          )
        : undefined;

      const inspectionStructured = {
        ...inspection,
        inspectionDate: inspectionDate,
        equipmentExternalId: inspection.bucketExternalId,
        isSynced: true,
      } as InspectionModel;
      inspectionStructured.inspectionCategoryTypes = inspectionCategoryTypes
        .map((category) => {
          const selectedCategory = categories.find(
            (c) => c.id === category.categoryId,
          );
          const selectedType = types.find((t) => t.id === category.typeId);

          if (!selectedCategory || !selectedType) {
            console.warn(
              `Category or Type not found for category.id: ${category.id}`,
            );
            return undefined;
          }

          const categoryStructured: InspectionCategoryTypeModel = {
            ...category,
            category: selectedCategory.name,
            type: selectedType.name,
            isSynced: true,
          } as InspectionCategoryTypeModel;

          const categoryObservations = observations.filter(
            (observation) => observation.categoryTypeId === category.id,
          );

          if (
            preserveExistingObservationsWhenPayloadEmpty &&
            !payloadContainsObservations &&
            categoryObservations.length === 0
          ) {
            const existingCategory =
              inspectionDb?.inspectionCategoryTypes?.find(
                (existing) =>
                  existing.externalId === category.externalId ||
                  existing.id === category.id,
              );
            categoryStructured.observations =
              existingCategory?.observations ?? [];
          } else {
            categoryStructured.observations = categoryObservations
              .map((observation) => {
                const observableItem = observationItems.find(
                  (item) => item.id === observation.observableItemId,
                );

                if (!observableItem) {
                  console.warn(
                    `ObservableItem not found for observation.id: ${observation.id}`,
                  );
                  return undefined;
                }

                const observationStructured = {
                  ...observation,
                  isSynced: true,
                  fileReceived: observation.fileReceived ?? false,
                  observableItem: observableItem,
                  observableItemId: observableItem.id,
                } as ObservationModel;

                return observationStructured;
              })
              .filter((obs): obs is ObservationModel => obs !== undefined);
          }
          return categoryStructured;
        })
        .filter((cat): cat is InspectionCategoryTypeModel => cat !== undefined);

      inspectionsStructured.push(inspectionStructured);
    }

    return inspectionsStructured;
  }

  return [];
}
